import React, { useState } from "react";
import { t } from "i18next";
import Icon from "../../../common/components/Icon";
import Loading from "../../../common/components/Loading";
import getCounterpartyBasic from "../../../api/getCounterpartyBasic";
import getAdataCompanyInfo from "../../../api/getAdataCompanyInfo";

const KOMPRA_LABELS = {
    name: "counterpartyName",
    fullName: "counterpartyFullName",
    bin: "counterpartyBin",
    rnn: "counterpartyRnn",
    iin: "counterpartyIin",
    field: "counterpartyField",
    factAddress: "counterpartyFactAddress",
    region: "counterpartyRegion",
    lawAddress: "counterpartyLawAddress",
    okpo: "counterpartyOkpo",
    oked: "counterpartyOked",
    owner: "counterpartyOwner",
    ownerIin: "counterpartyOwnerIin",
    registerDate: "counterpartyRegisterDate",
    workers: "counterpartyWorkers",
    size: "counterpartySize",
    krpCode: "counterpartyKrpCode",
    ownership: "counterpartyOwnership",
    kato: "counterpartyKato",
    city: "counterpartyCity",
    street: "counterpartyStreet",
    secondaryOked: "counterpartySecondaryOked",
    kbe: "counterpartyKbe",
    phone: "counterpartyPhone",
    email: "counterpartyEmail",
    website: "counterpartyWebsite",
    stateInvolvement: "counterpartyStateInvolvement",
    astanaHub: "counterpartyAstanaHub",
};

const ADATA_LABELS = {
    name_ru: "counterpartyFullName",
    short_name: "counterpartyName",
    biin: "counterpartyBin",
    legal_address: "counterpartyLawAddress",
    oked: "counterpartyOked",
    fullname_director: "counterpartyOwner",
    director_iin: "counterpartyOwnerIin",
    date_registration: "counterpartyRegisterDate",
    krp: "counterpartySize",
    employee_count: "counterpartyWorkers",
};

function formatDate(ts) {
    if (ts == null || ts === "") return "—";
    const d = new Date(Number(ts));
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatValue(value) {
    if (value == null || value === "") return "—";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
    return String(value);
}

function CounterpartyCheck({ setActiveTab }) {
    const [identifier, setIdentifier] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [kompraData, setKompraData] = useState(null);
    const [adataData, setAdataData] = useState(null);
    const [, setKompraError] = useState(null);
    const [, setAdataError] = useState(null);

    const handleBackClick = () => {
        setActiveTab?.(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setKompraError(null);
        setAdataError(null);
        setKompraData(null);
        setAdataData(null);
        const trimmed = identifier.trim();
        if (!trimmed) {
            setError(t("counterpartyEnterBin"));
            return;
        }
        setLoading(true);
        try {
            const [kompraResult, adataResult] = await Promise.allSettled([
                getCounterpartyBasic(trimmed),
                getAdataCompanyInfo(trimmed),
            ]);

            if (kompraResult.status === "fulfilled") {
                setKompraData(kompraResult.value);
            } else {
                setKompraError(kompraResult.reason?.message || t("counterpartyError"));
            }

            if (adataResult.status === "fulfilled") {
                setAdataData(adataResult.value);
            } else {
                setAdataError(adataResult.reason?.message || t("counterpartyError"));
            }

            if (kompraResult.status === "rejected" && adataResult.status === "rejected") {
                setError(t("counterpartyError"));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full max-w-[720px]">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="flex items-center gap-3 px-4 pt-4 pb-2 md:hidden">
                    <Icon src="backicon" onClick={handleBackClick} className="w-5 h-5 cursor-pointer flex-shrink-0" />
                    <span className="text-base font-semibold text-gray-800">{t('counterpartyCheck')}</span>
                </div>

            <div className="p-6">
                <form className="mb-6" onSubmit={handleSubmit}>
                    <label className="block text-sm font-semibold text-[var(--text-color,#333)] mb-2">
                        {t("counterpartyBinPlaceholder")}
                    </label>

                    <div className="flex flex-wrap gap-3 items-stretch">
                        <input
                            type="text"
                            className="flex-1 min-w-[160px] px-4 py-3 text-base border border-[#e0e0e0] rounded-[10px] outline-none"
                            placeholder="000000000000"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            disabled={loading}
                        />

                        <button
                            type="submit"
                            className="px-6 py-3 text-[15px] font-semibold text-white rounded-[10px] whitespace-nowrap"
                            style={{ background: 'var(--primary-color,#2e7d32)' }}
                            disabled={loading}
                        >
                            {loading ? t("counterpartySearching") : t("counterpartySearch")}
                        </button>
                    </div>
                </form>

                {error && !loading && (
                    <div className="flex items-center gap-2.5 p-3.5 border rounded-[10px] text-sm"
                         style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }}>
                        <span className="w-6 h-6 flex items-center justify-center rounded-full text-white font-bold text-sm"
                              style={{ background: '#b91c1c' }}>!</span>
                        {error}
                    </div>
                )}

                {loading && (
                    <div className="p-8 flex justify-center">
                        <Loading />
                    </div>
                )}

                {(kompraData || adataData) && !loading && (
                    <div className="flex flex-col gap-5 mt-4">
                        {[kompraData && {data: kompraData, type: 'kompra'}, adataData && {data: adataData, type: 'adata'}]
                            .filter(Boolean)
                            .map(({data, type}) => (
                                <div key={type} className="border border-[#e8e8e8] rounded-xl overflow-hidden">

                                    <div className="p-5 border-b border-[#e8e8e8]"
                                         style={{ background: 'linear-gradient(135deg, rgba(46,125,50,0.08), rgba(46,125,50,0.02))' }}>

                                        <span className="block text-xs font-semibold uppercase tracking-wider mb-1"
                                              style={{ color: 'var(--primary-color,#2e7d32)' }}>
                                            {type === 'kompra' ? 'Kompra.kz' : 'AData.kz'}
                                        </span>

                                        <span className="block text-lg font-bold text-[var(--text-color,#333)] mb-1">
                                            {type === 'kompra'
                                                ? (data.name || data.fullName || t("counterpartyNoName"))
                                                : (data.short_name || data.name_ru || t("counterpartyNoName"))}
                                        </span>

                                        {(data.bin || data.biin) && (
                                            <span className="text-sm font-semibold"
                                                  style={{ color: 'var(--primary-color,#2e7d32)' }}>
                                                БИН {data.bin || data.biin}
                                            </span>
                                        )}
                                    </div>

                                    <ul className="list-none m-0 p-0">
                                        {(type === 'kompra'
                                            ? Object.keys(KOMPRA_LABELS)
                                            : Object.keys(ADATA_LABELS)
                                        ).map((key) => {
                                            const raw = data[key];
                                            const value = key.includes('date') ? formatDate(raw) : formatValue(raw);
                                            if (value === "—") return null;

                                            const labelKey = type === 'kompra' ? KOMPRA_LABELS[key] : ADATA_LABELS[key];
                                            if (!labelKey) return null;

                                            return (
                                                <li key={key}
                                                    className="grid grid-cols-[140px_1fr] gap-3 px-5 py-3 border-b border-[#f0f0f0] items-baseline">

                                                    <span className="text-[13px] text-[#666] font-medium">
                                                        {t(labelKey)}
                                                    </span>

                                                    <span className="text-sm text-[var(--text-color,#333)] break-words">
                                                        {value}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))}
                    </div>
                )}

                {!kompraData && !adataData && !loading && !error && (
                    <div className="py-8 text-center">
                        <p className="text-[15px] text-[#666]">
                            {t("counterpartyEmptyHint")}
                        </p>
                    </div>
                )}
            </div>
            </div>
        </div>
    );
}

export default CounterpartyCheck;